"use client";

import { c as _c } from "react/compiler-runtime";
import type { AvailabilityFilter, OptionFilter, ProductFiltersResponse } from "@spree/sdk";
import { Check, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { getActiveFilterCount, getAvailabilityLabel } from "@/lib/utils/filters";
import type { PriceBucket } from "@/lib/utils/price-buckets";
import { findMatchingBucket } from "@/lib/utils/price-buckets";
import { type ActiveFilters, type AvailabilityStatus, isAvailabilityStatus } from "@/types/filters";
interface MobileFilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  filtersData: ProductFiltersResponse | null;
  activeFilters: ActiveFilters;
  priceBuckets: PriceBucket[];
  onApply: (filters: ActiveFilters) => void;
}
export function MobileFilterDrawer(t0) {
  const $ = _c(82);
  const {
    isOpen,
    onClose,
    filtersData,
    activeFilters,
    priceBuckets,
    onApply
  } = t0;
  const t = useTranslations("products");
  const [stagedFilters, setStagedFilters] = useState(activeFilters);
  let t1;
  if ($[0] !== activeFilters || $[1] !== isOpen) {
    t1 = () => {
      if (isOpen) {
        setStagedFilters(activeFilters);
      }
    };
    $[0] = activeFilters;
    $[1] = isOpen;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== isOpen) {
    t2 = [isOpen];
    $[3] = isOpen;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = optionValueId => {
      setStagedFilters(prev => {
        const newOptionValues = prev.optionValues.includes(optionValueId) ? prev.optionValues.filter(id => id !== optionValueId) : [...prev.optionValues, optionValueId];
        return {
          ...prev,
          optionValues: newOptionValues
        };
      });
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleOptionValueToggle = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = (min, max) => {
      setStagedFilters(prev_0 => ({
        ...prev_0,
        priceMin: min,
        priceMax: max
      }));
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handlePriceChange = t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = value => {
      setStagedFilters(prev_1 => ({
        ...prev_1,
        availability: value
      }));
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const handleAvailabilityChange = t5;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setStagedFilters(_temp);
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const handleClearAll = t6;
  let t7;
  if ($[9] !== onApply || $[10] !== onClose || $[11] !== stagedFilters) {
    t7 = () => {
      onApply(stagedFilters);
      onClose();
    };
    $[9] = onApply;
    $[10] = onClose;
    $[11] = stagedFilters;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const handleApply = t7;
  let T0;
  let T1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t8;
  let t9;
  if ($[13] !== filtersData?.filters || $[14] !== isOpen || $[15] !== onClose || $[16] !== priceBuckets || $[17] !== stagedFilters || $[18] !== t) {
    const stagedCount = getActiveFilterCount(stagedFilters);
    T1 = Sheet;
    t17 = isOpen;
    if ($[32] !== onClose) {
      t18 = open => {
        if (!open) {
          onClose();
        }
      };
      $[32] = onClose;
      $[33] = t18;
    } else {
      t18 = $[33];
    }
    T0 = SheetContent;
    t10 = "left";
    t11 = "w-full max-w-sm flex flex-col p-0 gap-0";
    t12 = false;
    t13 = undefined;
    let t19;
    if ($[34] !== t) {
      t19 = t("filters");
      $[34] = t;
      $[35] = t19;
    } else {
      t19 = $[35];
    }
    if ($[36] !== t19) {
      t14 = <SheetTitle className="sr-only">{t19}</SheetTitle>;
      $[36] = t19;
      $[37] = t14;
    } else {
      t14 = $[37];
    }
    let t20;
    if ($[38] !== t) {
      t20 = t("closeFilters");
      $[38] = t;
      $[39] = t20;
    } else {
      t20 = $[39];
    }
    let t21;
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <X className="w-6 h-6" />;
      $[40] = t21;
    } else {
      t21 = $[40];
    }
    let t22;
    if ($[41] !== onClose || $[42] !== t20) {
      t22 = <Button variant="ghost" size="icon" onClick={onClose} aria-label={t20}>{t21}</Button>;
      $[41] = onClose;
      $[42] = t20;
      $[43] = t22;
    } else {
      t22 = $[43];
    }
    let t23;
    if ($[44] !== t) {
      t23 = t("filters");
      $[44] = t;
      $[45] = t23;
    } else {
      t23 = $[45];
    }
    let t24;
    if ($[46] !== t23) {
      t24 = <h2 className="text-lg font-semibold uppercase">{t23}</h2>;
      $[46] = t23;
      $[47] = t24;
    } else {
      t24 = $[47];
    }
    let t25;
    if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
      t25 = <div className="w-10" />;
      $[48] = t25;
    } else {
      t25 = $[48];
    }
    if ($[49] !== t22 || $[50] !== t24) {
      t15 = <div className="flex items-center justify-between p-4 border-b border-gray-200">{t22}{t24}{t25}</div>;
      $[49] = t22;
      $[50] = t24;
      $[51] = t15;
    } else {
      t15 = $[51];
    }
    let t26;
    if ($[52] !== filtersData?.filters || $[53] !== priceBuckets || $[54] !== stagedFilters) {
      t26 = filtersData?.filters.map(filter => {
        switch (filter.type) {
          case "option":
            {
              return <MobileOptionSection key={filter.id} filter={filter as OptionFilter} selectedValues={stagedFilters.optionValues} onToggle={handleOptionValueToggle} />;
            }
          case "price_range":
            {
              return <MobilePriceSection key={filter.id} priceBuckets={priceBuckets} activeFilters={stagedFilters} onPriceChange={handlePriceChange} />;
            }
          case "availability":
            {
              return <MobileAvailabilitySection key={filter.id} filter={filter as AvailabilityFilter} selected={stagedFilters.availability} onChange={handleAvailabilityChange} />;
            }
          default:
            {
              return null;
            }
        }
      });
      $[52] = filtersData?.filters;
      $[53] = priceBuckets;
      $[54] = stagedFilters;
      $[55] = t26;
    } else {
      t26 = $[55];
    }
    if ($[56] !== t26) {
      t16 = <div className="flex-1 overflow-y-auto p-5 space-y-7">{t26}</div>;
      $[56] = t26;
      $[57] = t16;
    } else {
      t16 = $[57];
    }
    t8 = "border-t border-gray-200 p-4 space-y-2";
    t9 = stagedCount > 0 && <Button variant="ghost" className="w-full" onClick={handleClearAll}>{t("clearAllFiltersCount", {
        count: stagedCount
      })}</Button>;
    $[13] = filtersData?.filters;
    $[14] = isOpen;
    $[15] = onClose;
    $[16] = priceBuckets;
    $[17] = stagedFilters;
    $[18] = t;
    $[19] = T0;
    $[20] = T1;
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
    $[26] = t15;
    $[27] = t16;
    $[28] = t17;
    $[29] = t18;
    $[30] = t8;
    $[31] = t9;
  } else {
    T0 = $[19];
    T1 = $[20];
    t10 = $[21];
    t11 = $[22];
    t12 = $[23];
    t13 = $[24];
    t14 = $[25];
    t15 = $[26];
    t16 = $[27];
    t17 = $[28];
    t18 = $[29];
    t8 = $[30];
    t9 = $[31];
  }
  let t19;
  if ($[58] !== t) {
    t19 = t("showResults");
    $[58] = t;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  let t20;
  if ($[60] !== handleApply || $[61] !== t19) {
    t20 = <Button className="w-full" onClick={handleApply}>{t19}</Button>;
    $[60] = handleApply;
    $[61] = t19;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  let t21;
  if ($[63] !== t20 || $[64] !== t8 || $[65] !== t9) {
    t21 = <div className={t8}>{t9}{t20}</div>;
    $[63] = t20;
    $[64] = t8;
    $[65] = t9;
    $[66] = t21;
  } else {
    t21 = $[66];
  }
  let t22;
  if ($[67] !== T0 || $[68] !== t10 || $[69] !== t11 || $[70] !== t12 || $[71] !== t13 || $[72] !== t14 || $[73] !== t15 || $[74] !== t16 || $[75] !== t21) {
    t22 = <T0 side={t10} className={t11} showCloseButton={t12} aria-describedby={t13}>{t14}{t15}{t16}{t21}</T0>;
    $[67] = T0;
    $[68] = t10;
    $[69] = t11;
    $[70] = t12;
    $[71] = t13;
    $[72] = t14;
    $[73] = t15;
    $[74] = t16;
    $[75] = t21;
    $[76] = t22;
  } else {
    t22 = $[76];
  }
  let t23;
  if ($[77] !== T1 || $[78] !== t17 || $[79] !== t18 || $[80] !== t22) {
    t23 = <T1 open={t17} onOpenChange={t18}>{t22}</T1>;
    $[77] = T1;
    $[78] = t17;
    $[79] = t18;
    $[80] = t22;
    $[81] = t23;
  } else {
    t23 = $[81];
  }
  return t23;
}
function _temp(prev_2) {
  return {
    optionValues: [],
    priceMin: undefined,
    priceMax: undefined,
    availability: undefined,
    sortBy: prev_2.sortBy
  };
}
function MobileOptionSection(t0) {
  const $ = _c(10);
  const {
    filter,
    selectedValues,
    onToggle
  } = t0;
  const isColorFilter = filter.kind === "color_swatch";
  let t1;
  if ($[0] !== filter.label) {
    t1 = <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{filter.label}</h3>;
    $[0] = filter.label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== filter.options || $[3] !== isColorFilter || $[4] !== onToggle || $[5] !== selectedValues) {
    t2 = isColorFilter ? <div className="space-y-1">{filter.options.map(option => {
        const isSelected = selectedValues.includes(option.id);
        return <button key={option.id} type="button" aria-pressed={isSelected} onClick={() => onToggle(option.id)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${isSelected ? "bg-gray-50" : "hover:bg-gray-50"}`}><span className={`w-7 h-7 rounded-lg shrink-0 border-2 transition-colors overflow-hidden ${isSelected ? "border-gray-500 ring-2 ring-primary-200" : "border-gray-200"}`} style={option.image_url ? {
            backgroundImage: `url(${option.image_url})`,
            backgroundSize: "cover"
          } : option.color_code ? {
            backgroundColor: option.color_code
          } : {
            backgroundColor: "#e5e7eb"
          }} /><span className={`text-sm flex-1 text-left ${isSelected ? "font-medium text-gray-900" : "text-gray-700"}`}>{option.label}</span><span className="text-xs text-gray-400">({option.count})</span>{isSelected && <Check className="w-4 h-4 text-primary shrink-0" />}</button>;
      })}</div> : <div className="flex flex-wrap gap-2">{filter.options.map(option_0 => {
        const isSelected_0 = selectedValues.includes(option_0.id);
        return <button key={option_0.id} type="button" aria-pressed={isSelected_0} onClick={() => onToggle(option_0.id)} className={`px-3.5 py-2 text-sm rounded-xl border transition-colors ${isSelected_0 ? "border-gray-500 bg-primary text-white" : "border-gray-300 text-gray-700 hover:border-gray-400"}`}>{option_0.label}</button>;
      })}</div>;
    $[2] = filter.options;
    $[3] = isColorFilter;
    $[4] = onToggle;
    $[5] = selectedValues;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = <div>{t1}{t2}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
function MobilePriceSection(t0) {
  const $ = _c(20);
  const {
    priceBuckets,
    activeFilters,
    onPriceChange
  } = t0;
  const t = useTranslations("products");
  if (priceBuckets.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== activeFilters.priceMax || $[1] !== activeFilters.priceMin || $[2] !== priceBuckets) {
    t1 = findMatchingBucket(priceBuckets, activeFilters.priceMin, activeFilters.priceMax);
    $[0] = activeFilters.priceMax;
    $[1] = activeFilters.priceMin;
    $[2] = priceBuckets;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const selectedBucket = t1;
  let t2;
  if ($[4] !== t) {
    t2 = t("price");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{t2}</h3>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== onPriceChange || $[9] !== priceBuckets || $[10] !== selectedBucket?.id) {
    let t5;
    if ($[12] !== onPriceChange || $[13] !== selectedBucket?.id) {
      t5 = bucket => {
        const isSelected = selectedBucket?.id === bucket.id;
        return <button key={bucket.id} type="button" aria-pressed={isSelected} onClick={() => {
          if (isSelected) {
            onPriceChange(undefined, undefined);
          } else {
            onPriceChange(bucket.min, bucket.max);
          }
        }} className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-xl transition-colors ${isSelected ? "bg-gray-50 font-medium text-gray-900" : "text-gray-700 hover:bg-gray-50"}`}><span className="flex-1 text-left">{bucket.label}</span>{isSelected && <Check className="w-4 h-4 text-primary shrink-0" />}</button>;
      };
      $[12] = onPriceChange;
      $[13] = selectedBucket?.id;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    t4 = priceBuckets.map(t5);
    $[8] = onPriceChange;
    $[9] = priceBuckets;
    $[10] = selectedBucket?.id;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[15] !== t4) {
    t5 = <div className="space-y-1">{t4}</div>;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t3 || $[18] !== t5) {
    t6 = <div>{t3}{t5}</div>;
    $[17] = t3;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
}
function MobileAvailabilitySection(t0) {
  const $ = _c(18);
  const {
    filter,
    selected,
    onChange
  } = t0;
  const t = useTranslations("products");
  let t1;
  if ($[0] !== t) {
    t1 = t("availability");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{t1}</h3>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== filter.options || $[5] !== onChange || $[6] !== selected || $[7] !== t) {
    let t4;
    if ($[9] !== onChange || $[10] !== selected || $[11] !== t) {
      t4 = option => {
        const isSelected = selected === option.id;
        return <button key={option.id} type="button" aria-pressed={isSelected} onClick={() => {
          if (isSelected) {
            onChange(undefined);
          } else {
            if (isAvailabilityStatus(option.id)) {
              onChange(option.id);
            }
          }
        }} className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-xl transition-colors ${isSelected ? "bg-gray-50 font-medium text-gray-900" : "text-gray-700 hover:bg-gray-50"}`}><span className="flex-1 text-left">{getAvailabilityLabel(option.id, t)}</span><span className="text-xs text-gray-400">({option.count})</span>{isSelected && <Check className="w-4 h-4 text-primary shrink-0" />}</button>;
      };
      $[9] = onChange;
      $[10] = selected;
      $[11] = t;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    t3 = filter.options.map(t4);
    $[4] = filter.options;
    $[5] = onChange;
    $[6] = selected;
    $[7] = t;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[13] !== t3) {
    t4 = <div className="space-y-1">{t3}</div>;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== t2 || $[16] !== t4) {
    t5 = <div>{t2}{t4}</div>;
    $[15] = t2;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
}